import { useCallback } from "react";
import { MenuItemOptionElement } from "src/interfaces/menu.interface";
import {
  MenuItemOptionElementField,
  InputValue,
} from "src/types/menu-items.type";
import { MenuItemFormikProps } from "src/interfaces/form.interface";

function isValueEvent(value: unknown): value is { target: { value: string } } {
  return (
    typeof value === "object" &&
    value !== null &&
    "target" in (value as Record<string, unknown>) &&
    typeof (value as { target: { value: unknown } }).target?.value === "string"
  );
}

export const useOptionElementHandlers = (
  formik: MenuItemFormikProps,
  menuItemOptionIndex: number
) => {
  const { values, setFieldValue } = formik;
  const addNewOptionElementToForm = useCallback(() => {
    const newMenuItemOptionElement: MenuItemOptionElement = {
      name: "",
      nonEnglishName: "",
      description: "",
      renderSequence: 1,
      priceCents: 0,
    };

    const menuItemOption = values.itemOptionInputList?.[menuItemOptionIndex];
    if (!menuItemOption) {
      console.error(`Option at index ${menuItemOptionIndex} does not exist`);
      return;
    }

    const existingMenuItemOptionElements =
      menuItemOption.addItemOptionElementInputList || [];

    const newMenuItemOptionElements = [
      ...existingMenuItemOptionElements,
      newMenuItemOptionElement,
    ];

    setFieldValue(
      `itemOptionInputList[${menuItemOptionIndex}].addItemOptionElementInputList`,
      newMenuItemOptionElements
    );
  }, [menuItemOptionIndex, values.itemOptionInputList, setFieldValue]);

  const onChangeElementValue = useCallback(
    (
      menuItemOptionElementField: MenuItemOptionElementField,
      inputValue: InputValue,
      menuItemOptionElementIndex: number
    ) => {
      const menuItemOptions = structuredClone(values.itemOptionInputList ?? []);

      if (!menuItemOptions[menuItemOptionIndex]) {
        console.error(`Option at index ${menuItemOptionIndex} does not exist`);
        return;
      }

      if (!menuItemOptions[menuItemOptionIndex].addItemOptionElementInputList) {
        menuItemOptions[menuItemOptionIndex].addItemOptionElementInputList = [];
      }

      if (
        !menuItemOptions[menuItemOptionIndex].addItemOptionElementInputList[
          menuItemOptionElementIndex
        ]
      ) {
        menuItemOptions[menuItemOptionIndex].addItemOptionElementInputList[
          menuItemOptionElementIndex
        ] = {
          name: "",
          nonEnglishName: "",
          description: "",
          renderSequence: 1,
          priceCents: 0,
        };
      }

      const inputFieldValue = isValueEvent(inputValue)
        ? inputValue.target.value
        : inputValue;

      const targetElement =
        menuItemOptions[menuItemOptionIndex].addItemOptionElementInputList[
          menuItemOptionElementIndex
        ];

      if (menuItemOptionElementField === "renderSequence") {
        // Allow the user to type freely - preserve what they type
        if (inputFieldValue === "" || inputFieldValue == null) {
          // During typing/deleting, preserve null temporarily (don't force to 1)
          targetElement.renderSequence = null;
        } else {
          const renderSequence = Number(inputFieldValue);
          // Set the value if it's a valid number (even if 0 or negative during typing)
          // Validation will happen on blur
          if (Number.isFinite(renderSequence)) {
            targetElement.renderSequence = renderSequence;
          } else {
            // Invalid number, preserve null during typing
            targetElement.renderSequence = null;
          }
        }
      } else if (menuItemOptionElementField === "priceCents") {
        const priceCents = Number(inputFieldValue);
        targetElement.priceCents =
          inputFieldValue === "" ||
          inputFieldValue == null ||
          !Number.isFinite(priceCents)
            ? 0
            : priceCents;
      } else if (menuItemOptionElementField === "name") {
        targetElement.name = String(inputFieldValue ?? "");
      } else if (menuItemOptionElementField === "nonEnglishName") {
        targetElement.nonEnglishName = String(inputFieldValue ?? "");
      } else if (menuItemOptionElementField === "description") {
        targetElement.description = String(inputFieldValue ?? "");
      }

      setFieldValue("itemOptionInputList", menuItemOptions);
    },
    [menuItemOptionIndex, values.itemOptionInputList, setFieldValue]
  );

  const deleteOptionElementFromForm = useCallback(
    (menuItemOptionElementIndex: number) => {
      const menuItemOptions = structuredClone(values.itemOptionInputList ?? []);

      if (!menuItemOptions[menuItemOptionIndex]) {
        console.error(`Option at index ${menuItemOptionIndex} does not exist`);
        return;
      }

      if (!menuItemOptions[menuItemOptionIndex].addItemOptionElementInputList) {
        menuItemOptions[menuItemOptionIndex].addItemOptionElementInputList = [];
      }

      if (
        menuItemOptions[menuItemOptionIndex].addItemOptionElementInputList
          .length > 1
      ) {
        menuItemOptions[
          menuItemOptionIndex
        ].addItemOptionElementInputList.splice(menuItemOptionElementIndex, 1);
      } else {
        // When the last element is deleted, leave the list empty
        // menuItemOptions[menuItemOptionIndex].addItemOptionElementInputList = [
        //   {
        //     name: "",
        //     description: "",
        //     renderSequence: 0,
        //     priceCents: 0,
        //   },
        // ];
        menuItemOptions[menuItemOptionIndex].addItemOptionElementInputList = [];
      }
      setFieldValue("itemOptionInputList", menuItemOptions);
    },
    [menuItemOptionIndex, values.itemOptionInputList, setFieldValue]
  );

  return {
    addNewOptionElementToForm,
    onChangeElementValue,
    deleteOptionElementFromForm,
  } as const;
};
